import { STORAGE_KEY } from './constants';

function readToken(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.recipientToken === 'string' ? parsed.recipientToken : null;
  } catch {
    return null;
  }
}

function appendRelative(input: string, token: string): string {
  // Split off hash and query without losing relative shape.
  const hashIdx = input.indexOf('#');
  const hash = hashIdx >= 0 ? input.slice(hashIdx) : '';
  const noHash = hashIdx >= 0 ? input.slice(0, hashIdx) : input;

  const queryIdx = noHash.indexOf('?');
  const path = queryIdx >= 0 ? noHash.slice(0, queryIdx) : noHash;
  const queryStr = queryIdx >= 0 ? noHash.slice(queryIdx + 1) : '';

  const params = new URLSearchParams(queryStr);
  if (params.has('r')) return input;
  params.set('r', token);

  return `${path}?${params.toString()}${hash}`;
}

export function withRecipientToken(input: string): string {
  if (!input) return input;

  const token = readToken();
  if (!token) return input;

  try {
    // Protocol-relative URLs (//host/path) inherit the page's scheme and are
    // ALWAYS treated as external — never stamp. Without this guard, `new URL('//foo')`
    // throws, falls through to relative-append, and leaks the recipient token to
    // an external host (P5-D10-F6 review fix).
    if (input.startsWith('//')) return input;

    // Detect absolute URL by attempting a parse with no base.
    let parsed: URL | null = null;
    try {
      parsed = new URL(input);
    } catch {
      parsed = null;
    }

    if (parsed) {
      // Only http/https are eligible for attribution. mailto:, tel:, javascript: bail.
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return input;
      }
      // External-origin guard.
      if (parsed.origin !== window.location.origin) return input;
      // URL-author wins.
      if (parsed.searchParams.has('r')) return input;
      parsed.searchParams.set('r', token);
      return parsed.toString();
    }

    // Relative input: keep relative shape.
    return appendRelative(input, token);
  } catch {
    return input;
  }
}
